import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Upload, ArrowRight, Sparkles, Landmark } from "lucide-react";
import { FISCAL_FIELDS, FiscalFields } from "@/components/FiscalDataSection";

const TOTAL_STEPS = 5;

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const TAX_OPTIONS = [
  { id: "none", label: "Sin impuesto", desc: "0%" },
  { id: "iva", label: "IVA", desc: "21% Península" },
  { id: "igic", label: "IGIC", desc: "7% Canarias" },
];

const COLOR_PRESETS = ["#EA580C", "#2563EB", "#16A34A", "#9333EA", "#0891B2", "#DC2626", "#0F172A", "#CA8A04"];

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    business_name: user?.business_name || "",
    full_name: user?.full_name || "",
    phone: user?.phone || "",
    default_tax_type: user?.default_tax_type || "iva",
    brand_color: user?.brand_color || "#EA580C",
    iban: user?.iban || "",
  });
  const [logoSrc, setLogoSrc] = useState(user?.logo_url ? `${BACKEND}${user.logo_url}` : null);

  const [fiscal, setFiscal] = useState(() => Object.fromEntries(FISCAL_FIELDS.map(([k]) => [k, user?.[k] || ""])));
  const [savingFiscal, setSavingFiscal] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const saveFiscalAndNext = async () => {
    if (!Object.values(fiscal).some((v) => v.trim())) return setStep(3);
    setSavingFiscal(true);
    try {
      await api.put("/profile", fiscal);
      toast.success("Datos fiscales guardados · ya puedes facturar");
      setStep(3);
    } catch (e) { toast.error(e?.response?.data?.detail || "Revisa los datos fiscales"); }
    finally { setSavingFiscal(false); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/profile/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setLogoSrc(`${BACKEND}${data.logo_url}`);
    toast.success("Logo subido");
  };

  const finish = async (skip = false) => {
    if (skip) {
      await api.post("/onboarding/skip");
    } else {
      await api.post("/onboarding/complete", form);
    }
    await refresh();
    toast.success("¡Todo listo! Vamos al panel");
    nav("/dashboard");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="onboarding-page">
      <div className="bg-white border border-slate-200 max-w-2xl w-full">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="kbar">Bienvenido · Paso {step} de {TOTAL_STEPS}</div>
            <h1 className="font-display font-extrabold text-2xl tracking-tight mt-1">Configura tu cuenta</h1>
          </div>
          <button onClick={() => finish(true)} data-testid="onboarding-skip" className="text-sm text-slate-500 hover:text-slate-700">Saltar configuración →</button>
        </div>

        <div className="px-6 pt-2">
          <div className="flex gap-1">
            {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((i) => (
              <div key={i} className={`h-1 flex-1 ${i <= step ? "bg-orange-600" : "bg-slate-200"}`} />
            ))}
          </div>
        </div>

        <div className="p-8 min-h-[320px]">
          {step === 1 && (
            <div data-testid="step-1" className="space-y-4">
              <h2 className="font-display font-bold text-xl">Datos de tu negocio</h2>
              <p className="text-sm text-slate-500">Aparecerán en tus presupuestos y emails.</p>
              <Field testid="ob-business" label="Nombre del negocio" value={form.business_name} onChange={set("business_name")} placeholder="Reformas García" />
              <Field testid="ob-fullname" label="Tu nombre completo" value={form.full_name} onChange={set("full_name")} />
              <Field testid="ob-phone" label="Teléfono" value={form.phone} onChange={set("phone")} placeholder="+34 600 000 000" />
              <Field testid="ob-iban" label="IBAN / Cuenta corriente (opcional)" value={form.iban} onChange={set("iban")} placeholder="ES00 0000 0000 0000 0000 0000" />
            </div>
          )}

          {step === 2 && (
            <div data-testid="step-fiscal" className="space-y-4">
              <h2 className="font-display font-bold text-xl inline-flex items-center gap-2"><Landmark className="w-5 h-5 text-orange-600" /> Datos fiscales</h2>
              <p className="text-sm text-slate-500">Obligatorios para emitir facturas legales (RD 1619/2012). Aparecerán en la cabecera de tus facturas y presupuestos.</p>
              <FiscalFields form={fiscal} setForm={setFiscal} />
              <button type="button" onClick={() => setStep(3)} data-testid="ob-fiscal-later" className="text-sm font-semibold text-slate-500 hover:text-slate-900 underline underline-offset-4">
                Lo haré luego
              </button>
            </div>
          )}

          {step === 3 && (
            <div data-testid="step-3" className="space-y-4">
              <h2 className="font-display font-bold text-xl">Sube tu logo</h2>
              <p className="text-sm text-slate-500">Aparecerá en los PDFs y la página pública del presupuesto. Opcional.</p>
              <div className="flex items-center gap-6">
                {logoSrc ? (
                  <img src={logoSrc} alt="logo" className="w-32 h-32 object-contain border border-slate-200" data-testid="ob-logo-img" />
                ) : (
                  <div className="w-32 h-32 border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-300">Sin logo</div>
                )}
                <label className="inline-flex items-center gap-2 h-11 px-4 border border-slate-200 hover:border-slate-300 cursor-pointer text-sm font-semibold" data-testid="ob-upload-logo">
                  <Upload className="w-4 h-4" /> Subir logo
                  <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={uploadLogo} />
                </label>
              </div>
            </div>
          )}

          {step === 4 && (
            <div data-testid="step-4" className="space-y-4">
              <h2 className="font-display font-bold text-xl">Gravamen predeterminado</h2>
              <p className="text-sm text-slate-500">Se aplicará automáticamente en tus presupuestos. Lo puedes cambiar en cada uno.</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-2">
                {TAX_OPTIONS.map((t) => (
                  <button key={t.id} type="button" data-testid={`ob-tax-${t.id}`} onClick={() => setForm({ ...form, default_tax_type: t.id })}
                    className={`p-4 text-left border transition-colors ${form.default_tax_type === t.id ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-slate-300"}`}>
                    <div className="font-bold">{t.label}</div>
                    <div className={`text-xs ${form.default_tax_type === t.id ? "text-slate-300" : "text-slate-500"}`}>{t.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 5 && (
            <div data-testid="step-5" className="space-y-4">
              <h2 className="font-display font-bold text-xl">Color corporativo</h2>
              <p className="text-sm text-slate-500">Se usará en los PDFs y emails. Elige uno o pega un código hex.</p>
              <div className="grid grid-cols-8 gap-2 pt-2">
                {COLOR_PRESETS.map((c) => (
                  <button key={c} type="button" data-testid={`ob-color-${c.replace("#","")}`} onClick={() => setForm({ ...form, brand_color: c })}
                    className={`w-12 h-12 transition-all ${form.brand_color === c ? "ring-4 ring-offset-2 ring-slate-300 scale-105" : "hover:scale-105"}`}
                    style={{ background: c }} />
                ))}
              </div>
              <div className="flex items-center gap-3 pt-2">
                <input data-testid="ob-color-custom" value={form.brand_color} onChange={set("brand_color")} placeholder="#EA580C" className="h-11 px-3 border border-slate-200 focus:border-orange-600 outline-none w-40 font-mono" />
                <div className="h-11 w-11 border border-slate-200" style={{ background: form.brand_color }} />
              </div>
              <div className="mt-6 p-5 border border-slate-200" style={{ borderTopWidth: 4, borderTopColor: form.brand_color }}>
                <div className="kbar" style={{ color: form.brand_color }}>VISTA PREVIA</div>
                <div className="font-display font-extrabold text-xl mt-1">Presupuesto de ejemplo</div>
                <div className="mt-3 inline-flex items-center gap-2 text-white px-4 py-2 text-sm font-bold" style={{ background: form.brand_color }}>
                  TOTAL · 1.234,56 €
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-slate-200 flex justify-between items-center">
          <button onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1} className="h-11 px-4 text-sm font-semibold disabled:opacity-30">← Atrás</button>
          {step < TOTAL_STEPS ? (
            <button onClick={() => (step === 2 ? saveFiscalAndNext() : setStep(step + 1))} disabled={savingFiscal} data-testid="onboarding-next" className="btn-brand h-11 px-6 inline-flex items-center gap-2">Siguiente <ArrowRight className="w-4 h-4" /></button>
          ) : (
            <button onClick={() => finish(false)} data-testid="onboarding-finish" className="btn-brand h-11 px-6 inline-flex items-center gap-2"><Sparkles className="w-4 h-4" /> ¡Empezar a usar!</button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, ...rest }) {
  return (
    <div>
      <label className="kbar block">{label}</label>
      <input data-testid={testid} className="w-full h-12 mt-2 px-3 border border-slate-200 focus:border-orange-600 outline-none" {...rest} />
    </div>
  );
}
